import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useActiveTenantId } from '@/store/tenantStore'
import {
  Badge,
  Input,
  Switch,
  Textarea,
  useToast,
} from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { Organization } from '../api/organizationsApi'
import { organizationsApi } from '../api/organizationsApi'

type OrgForm = {
  code: string
  name: string
  description: string
  isActive: boolean
}

export default function OrganizationsPage() {
  const [searchParams] = useSearchParams()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId
  const { toast } = useToast()

  return (
    <CrudPage<Organization, OrgForm, any, any>
      title="Organizasyon Birimleri"
      subtitle="HR organizasyon birimlerini yönet"
      primaryActionLabel="Yeni Birim"
      onExportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de export endpoint'i yok. TODO: GET /api/v1/organizations/export (Reporting).",
          variant: 'warning',
        })
      }
      onImportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de import endpoint'i yok. TODO: POST /api/v1/organizations/import (HR write).",
          variant: 'warning',
        })
      }
      list={async (p) =>
        organizationsApi.list({
          tenantId,
          search: p.search,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: (p.sortBy as any) ?? undefined,
          sortDir: (p.sortDir as any) ?? undefined,
          // Filters
          isActive:
            p.filters.status === 'all' ? null : p.filters.status === 'active',
          includeDeleted: p.filters.deleted === 'show',
        })
      }
      getRowId={(o) => o.organizationId}
      columns={[
        {
          key: 'code',
          header: 'Kod',
          sortable: true,
          widthClassName: 'w-[160px]',
          render: (o) => <span className="font-medium">{o.code ?? '-'}</span>,
        },
        {
          key: 'name',
          header: 'Adı',
          sortable: true,
          widthClassName: 'w-[260px]',
          render: (o) => o.name,
        },
        {
          key: 'description',
          header: 'Açıklama',
          sortable: false,
          render: (o) => <span className="truncate block max-w-[520px]">{o.description ?? '-'}</span>,
        },
        {
          key: 'isActive',
          header: 'Durum',
          sortable: false,
          widthClassName: 'w-[140px]',
          render: (o) =>
            o.isActive ? <Badge variant="success">Aktif</Badge> : <Badge variant="warning">Pasif</Badge>,
        },
      ]}
      searchPlaceholder="Ara (kod, ad, açıklama)…"
      filters={[
        {
          id: 'status',
          label: 'Durum',
          value: 'all',
          options: [
            { label: 'Tümü', value: 'all' },
            { label: 'Aktif', value: 'active' },
            { label: 'Pasif', value: 'inactive' },
          ],
        },
        {
          id: 'deleted',
          label: 'Silinenler',
          value: 'hide',
          options: [
            { label: 'Gizle', value: 'hide' },
            { label: 'Göster', value: 'show' },
          ],
          widthClassName: 'min-w-[160px]',
        },
      ]}
      initialPageSize={10}
      modalTitles={{ create: 'Yeni Organizasyon', edit: 'Organizasyonu Düzenle' }}
      getCreateDefaultForm={() => ({ code: '', name: '', description: '', isActive: true })}
      getEditForm={(o) => ({
        code: o.code ?? '',
        name: o.name ?? '',
        description: o.description ?? '',
        isActive: o.isActive,
      })}
      validate={(f) => ({
        code: f.code.trim().length > 50 ? 'Kod en fazla 50 karakter olmalıdır' : undefined,
        name: !f.name.trim() ? 'Birim Adı zorunludur' : f.name.trim().length > 255 ? 'Ad en fazla 255 karakter olmalıdır' : undefined,
        description: f.description.trim().length > 1000 ? 'Açıklama en fazla 1000 karakter olmalıdır' : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <Input
            label="Kod"
            value={form.code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, code: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.code}
            helperText={touched ? errors.code : undefined}
            required
            disabled={isSubmitting}
            placeholder="Organizasyon kodu"
            leftIcon={<i className="material-symbols-outlined text-lg">tag</i>}
          />
          <Input
            label="Adı"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, name: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            required
            disabled={isSubmitting}
            placeholder="Organizasyon adı"
            leftIcon={<i className="material-symbols-outlined text-lg">business</i>}
          />
          <Textarea
            label="Açıklama"
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((s) => ({ ...s, description: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.description}
            helperText={touched ? errors.description : undefined}
            disabled={isSubmitting}
            placeholder="Organizasyon açıklaması"
          />
          <Switch
            checked={form.isActive}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isActive: v }))}
            label="Aktif"
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        tenantId,
        code: f.code.trim() ? f.code.trim() : null,
        name: f.name.trim(),
        description: f.description.trim() ? f.description.trim() : null,
        isActive: f.isActive,
      })}
      buildUpdatePayload={(f) => ({
        tenantId,
        code: f.code.trim() ? f.code.trim() : null,
        name: f.name.trim(),
        description: f.description.trim() ? f.description.trim() : null,
        isActive: f.isActive,
      })}
      create={(payload) => organizationsApi.create(payload)}
      update={(id, payload) => organizationsApi.update({ id: Number(id), ...(payload as any) })}
      remove={(id) => organizationsApi.delete(Number(id))}
      getCreatedId={(res) => res?.data?.id}
    />
  )
}

